import React, { useState } from 'react';
import { Collapse, Form, Input, Slider, Select, Radio, Space, Button, InputNumber, Tag, Tooltip } from 'antd';
import {
  BgColorsOutlined, FontSizeOutlined, ColumnWidthOutlined, UnorderedListOutlined,
  PlusOutlined, DeleteOutlined, LineOutlined,
} from '@ant-design/icons';

const { Panel } = Collapse;

export interface ReferenceLine {
  id: string;
  direction: 'horizontal' | 'vertical';
  value: number;
  label: string;
  color: string;
  lineStyle: 'solid' | 'dashed' | 'dotted';
}

export interface ChartStyleOverrides {
  title?: string;
  colorTheme?: string;
  titleFontSize?: number;
  labelFontSize?: number;
  xAxisName?: string;
  yAxisName?: string;
  yAxisMin?: number | undefined;
  yAxisMax?: number | undefined;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  referenceLines?: ReferenceLine[];
}

export const COLOR_THEMES: Record<string, { label: string; colors: string[] }> = {
  default: {
    label: '默认紫蓝',
    colors: ['#533afd', '#6b54fd', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#fa709a'],
  },
  business: {
    label: '商务蓝',
    colors: ['#1565c0', '#42a5f5', '#90caf9', '#0d47a1', '#5c6bc0', '#7986cb'],
  },
  vibrant: {
    label: '活力橙',
    colors: ['#ff6d00', '#ff9100', '#ffab40', '#ffd740', '#ff3d00', '#dd2c00'],
  },
  nature: {
    label: '自然绿',
    colors: ['#2e7d32', '#43a047', '#66bb6a', '#81c784', '#a5d6a7', '#1b5e20'],
  },
  classic: {
    label: '经典灰',
    colors: ['#455a64', '#546e7a', '#78909c', '#90a4ae', '#b0bec5', '#37474f'],
  },
};

const PRESET_LINES = [
  { label: 'USL (规格上限)', color: '#df1b41' },
  { label: 'LSL (规格下限)', color: '#15be53' },
  { label: 'Target (目标值)', color: '#ff9800' },
  { label: '均值线', color: '#533afd' },
];

interface ChartStyleEditorProps {
  style: ChartStyleOverrides;
  onChange: (style: ChartStyleOverrides) => void;
  compact?: boolean;
}

const ChartStyleEditor: React.FC<ChartStyleEditorProps> = ({ style, onChange, compact = false }) => {
  const [newLineDirection, setNewLineDirection] = useState<'horizontal' | 'vertical'>('horizontal');
  const [newLineValue, setNewLineValue] = useState<number | null>(null);
  const [newLineLabel, setNewLineLabel] = useState('');
  const [newLineColor, setNewLineColor] = useState('#df1b41');
  const [newLineStyle, setNewLineStyle] = useState<'solid' | 'dashed' | 'dotted'>('dashed');

  const update = (patch: Partial<ChartStyleOverrides>) => {
    onChange({ ...style, ...patch });
  };

  const lines = style.referenceLines || [];

  const addLine = () => {
    if (newLineValue === null) return;
    const newLine: ReferenceLine = {
      id: `ref_${Date.now()}`,
      direction: newLineDirection,
      value: newLineValue,
      label: newLineLabel || `${newLineDirection === 'horizontal' ? 'Y' : 'X'}=${newLineValue}`,
      color: newLineColor,
      lineStyle: newLineStyle,
    };
    update({ referenceLines: [...lines, newLine] });
    setNewLineValue(null);
    setNewLineLabel('');
  };

  const addPresetLine = (preset: typeof PRESET_LINES[0]) => {
    setNewLineLabel(preset.label);
    setNewLineColor(preset.color);
    setNewLineDirection('horizontal');
    setNewLineStyle('dashed');
  };

  const removeLine = (id: string) => {
    update({ referenceLines: lines.filter(l => l.id !== id) });
  };

  return (
    <div className="card-trendata" style={{ padding: compact ? 10 : 16, marginTop: compact ? 8 : 16 }}>
      <div className="title-bar" style={{ marginBottom: compact ? 6 : 12 }}>
        <h3 style={{ fontSize: compact ? 13 : 15, fontWeight: 600, color: '#061b31', margin: 0 }}>
          样式编辑
        </h3>
      </div>
      <Collapse
        bordered={false}
        defaultActiveKey={compact ? [] : ['title', 'color']}
        style={{ background: 'transparent' }}
        size="small"
      >
        <Panel
          header={<span><FontSizeOutlined style={{ marginRight: 8 }} />标题与字号</span>}
          key="title"
        >
          <Form layout="vertical" size="small">
            <Form.Item label="图表标题" style={{ marginBottom: 12 }}>
              <Input
                value={style.title || ''}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="输入图表标题"
                allowClear
              />
            </Form.Item>
            <Form.Item label={`标题字号: ${style.titleFontSize || 18}px`} style={{ marginBottom: 12 }}>
              <Slider
                min={12}
                max={24}
                value={style.titleFontSize || 18}
                onChange={(v) => update({ titleFontSize: v })}
              />
            </Form.Item>
            <Form.Item label={`标签字号: ${style.labelFontSize || 12}px`} style={{ marginBottom: 8 }}>
              <Slider
                min={10}
                max={16}
                value={style.labelFontSize || 12}
                onChange={(v) => update({ labelFontSize: v })}
              />
            </Form.Item>
          </Form>
        </Panel>

        <Panel
          header={<span><BgColorsOutlined style={{ marginRight: 8 }} />颜色主题</span>}
          key="color"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {Object.entries(COLOR_THEMES).map(([key, theme]) => (
              <div
                key={key}
                onClick={() => update({ colorTheme: key })}
                style={{
                  padding: compact ? '6px 8px' : '8px 12px',
                  borderRadius: 8, cursor: 'pointer',
                  border: style.colorTheme === key ? '2px solid #533afd' : '2px solid transparent',
                  background: style.colorTheme === key ? '#f0f2ff' : '#fafafa',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{theme.label}</div>
                <Space size={4}>
                  {theme.colors.slice(0, compact ? 4 : 6).map((c, i) => (
                    <div key={i} style={{ width: compact ? 16 : 20, height: compact ? 16 : 20, borderRadius: 4, background: c }} />
                  ))}
                </Space>
              </div>
            ))}
          </Space>
        </Panel>

        {/* 参考线面板 */}
        <Panel
          header={<span><LineOutlined style={{ marginRight: 8 }} />参考线 {lines.length > 0 && <Tag color="purple" style={{ marginLeft: 4 }}>{lines.length}</Tag>}</span>}
          key="reflines"
        >
          {/* 已添加的参考线 */}
          {lines.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {lines.map(line => (
                <div key={line.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', borderRadius: 6, marginBottom: 6,
                  background: '#fafafa', border: '1px solid #f0f0f0',
                }}>
                  <div style={{
                    width: 24, borderRadius: 1,
                    ...(line.lineStyle === 'solid'
                      ? { height: 3, background: line.color }
                      : { height: 0, borderTop: `2px ${line.lineStyle} ${line.color}` }),
                  }} />
                  <span style={{ fontSize: 12, flex: 1, fontWeight: 500 }}>{line.label}</span>
                  <Tag color={line.direction === 'horizontal' ? 'blue' : 'green'} style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>
                    {line.direction === 'horizontal' ? `Y=${line.value}` : `X=${line.value}`}
                  </Tag>
                  <Tooltip title="删除">
                    <Button type="text" size="small" danger icon={<DeleteOutlined />}
                      onClick={() => removeLine(line.id)} style={{ padding: '0 4px' }} />
                  </Tooltip>
                </div>
              ))}
            </div>
          )}

          {/* 快捷预设 */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>快捷添加：</div>
            <Space size={4} wrap>
              {PRESET_LINES.map(p => (
                <Tag key={p.label} color="default"
                  style={{ cursor: 'pointer', fontSize: 11 }}
                  onClick={() => addPresetLine(p)}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: p.color, marginRight: 4 }} />
                  {p.label}
                </Tag>
              ))}
            </Space>
          </div>

          {/* 新增参考线表单 */}
          <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Select size="small" value={newLineDirection} onChange={setNewLineDirection} style={{ width: 90 }}>
                <Select.Option value="horizontal">水平线</Select.Option>
                <Select.Option value="vertical">垂直线</Select.Option>
              </Select>
              <InputNumber size="small" placeholder="数值" value={newLineValue}
                onChange={(v) => setNewLineValue(v)} style={{ flex: 1 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Input size="small" placeholder="标签名称（可选）" value={newLineLabel}
                onChange={(e) => setNewLineLabel(e.target.value)} style={{ flex: 1 }} />
              <Select size="small" value={newLineStyle} onChange={setNewLineStyle} style={{ width: 80 }}>
                <Select.Option value="solid">实线</Select.Option>
                <Select.Option value="dashed">虚线</Select.Option>
                <Select.Option value="dotted">点线</Select.Option>
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#666' }}>颜色：</span>
              <Space size={4}>
                {['#df1b41', '#15be53', '#ff9800', '#533afd', '#333333'].map(c => (
                  <div key={c} onClick={() => setNewLineColor(c)} style={{
                    width: 20, height: 20, borderRadius: 4, background: c, cursor: 'pointer',
                    border: newLineColor === c ? '2px solid #061b31' : '2px solid transparent',
                  }} />
                ))}
              </Space>
              <div style={{ flex: 1 }} />
              <Button type="primary" size="small" icon={<PlusOutlined />}
                onClick={addLine} disabled={newLineValue === null}>
                添加
              </Button>
            </div>
          </div>
        </Panel>

        {!compact && (
          <Panel
            header={<span><ColumnWidthOutlined style={{ marginRight: 8 }} />坐标轴设置</span>}
            key="axis"
          >
            <Form layout="vertical" size="small">
              <Form.Item label="X 轴名称" style={{ marginBottom: 12 }}>
                <Input
                  value={style.xAxisName ?? ''}
                  onChange={(e) => update({ xAxisName: e.target.value })}
                  placeholder="X 轴名称"
                  allowClear
                />
              </Form.Item>
              <Form.Item label="Y 轴名称" style={{ marginBottom: 12 }}>
                <Input
                  value={style.yAxisName ?? ''}
                  onChange={(e) => update({ yAxisName: e.target.value })}
                  placeholder="Y 轴名称"
                  allowClear
                />
              </Form.Item>
              <Space>
                <Form.Item label="Y 轴最小值" style={{ marginBottom: 8 }}>
                  <Input
                    type="number"
                    value={style.yAxisMin ?? ''}
                    onChange={(e) => update({ yAxisMin: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="自动"
                    style={{ width: 100 }}
                  />
                </Form.Item>
                <Form.Item label="Y 轴最大值" style={{ marginBottom: 8 }}>
                  <Input
                    type="number"
                    value={style.yAxisMax ?? ''}
                    onChange={(e) => update({ yAxisMax: e.target.value ? Number(e.target.value) : undefined })}
                    placeholder="自动"
                    style={{ width: 100 }}
                  />
                </Form.Item>
              </Space>
            </Form>
          </Panel>
        )}

        <Panel
          header={<span><UnorderedListOutlined style={{ marginRight: 8 }} />图例位置</span>}
          key="legend"
        >
          <Radio.Group
            value={style.legendPosition || 'top'}
            onChange={(e) => update({ legendPosition: e.target.value })}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="top">上</Radio.Button>
            <Radio.Button value="bottom">下</Radio.Button>
            <Radio.Button value="left">左</Radio.Button>
            <Radio.Button value="right">右</Radio.Button>
          </Radio.Group>
        </Panel>
      </Collapse>
    </div>
  );
};

export default ChartStyleEditor;
